import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { CloseOutlined, SaveOutlined, UserOutlined } from "@ant-design/icons";
import HTTPService from "../../../utils/makerequest";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { useTranslation } from 'react-i18next';
import { ICampusData } from "../../../interfaces/ICampusData";
import { Toast } from 'primereact/toast';

const AddCampus: React.FC<ICampusData> = ({ cancelModal, savedObj, companyId, campusId, campusName, }) => {
  const { t } = useTranslation();
  const toast = useRef<any>(null);
  const [companyData, setCompanyData] = useState<ICompanyData[]>([]);
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const isCompanyIdAvailable = !!companyId;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await HTTPService.GETCompanyNames();
      setCompanyData(result);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('fetch_company_names_failed'),
        life: 3000
      });
    }
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };


  const CampusRegister = async (values) => {
    try {
      const companyIdToUse = userInfo?.role === UserRole.AltisAdmin
        ? values.selectedCompany
        : userInfo?.companyId || companyId;

      const newGroup = {
        campusName: values.campusName,
        companyId: companyIdToUse || companyId,
      };
      const res = await HTTPService.POST(`/Campus/registerCampus`, newGroup);

      if (res.status === 200) {
        const currentUrl = window.location.href;
        const redirectUrl = 'admin/add-campus';
        if (currentUrl.includes(redirectUrl)) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t('successful'),
            life: 3000
          });
          window.location.replace('admin/campus-management');
        }
        else {
          savedObj(res.data)
          cancelModal();
        }
      } else if (res.status === 450) {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('error450'),
          life: 3000
        });
      }
      else {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t(res.data),
          life: 3000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: t('mistake'),
        detail: t('add_campus_failed'),
        life: 3000
      });
    }
  };

  const handleCancel = () => {
    const currentUrl = window.location.href;
    const addInventoryUrl = 'admin/add-inventory';
    const updateInventoryUrl = 'admin/update-inventory';
    const inventoryManage = 'admin/inventory-management';

    if (currentUrl.includes(addInventoryUrl)) {
      cancelModal();
    } else if (currentUrl.includes(updateInventoryUrl)) {
      cancelModal();
    } else if (currentUrl.includes(inventoryManage)) {
      cancelModal();
    }
    else {
      window.location.replace('/admin/campus-management');
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Card style={{ padding: "50px" }}>
        <h3>{t('register_campus')}</h3>
        <Form onFinish={CampusRegister} layout="vertical"
          fields={[
            { name: ["selectedCompany"], value: companyId },
          ]}
        >
          {!isCompanyIdAvailable && userInfo?.role === UserRole.AltisAdmin && (
            <Form.Item
              name='selectedCompany'
              label={t('Select company')}
              rules={[
                {
                  required: true,
                  message: t('please_select_a_company'),
                },
              ]}
            >
              <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}>
                {companyData.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Form.Item
                name="campusName"
                label={t('campus')}
                rules={[
                  {
                    required: true,
                    message: t('please_input_your_campus'),
                    whitespace: true,
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t('campus')}
                  maxLength={50}
                  style={{ width: '100%', borderRadius: 0 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
              <Col xs={12} sm={4}>
                <Button
                  block
                  onClick={handleCancel}
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <CloseOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('cancel')}
                  </span>
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  block
                  htmlType="submit"
                  type="primary"
                  style={{
                    height: 64,
                    backgroundColor: 'transparent',
                    color: '#1890ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                    e.currentTarget.style.color = '#40a9ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                >
                  <SaveOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {t('save')}
                  </span>
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>
    </>

  );
};

export default AddCampus;
